import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { Boton } from '#components/mobile/ui/Boton';
import { BotonRedondo } from '#components/mobile/ui/Cabecera';
import { color, espacio, radio, texto } from '#components/mobile/ui/tokens';
import { useLanguage } from '#hooks/useLocale';
import { pedirFotoPuente, pedirPuente } from '#uso';

import { fechaCorta, inicialDe, nombrePantalla } from './puente';
import type { FilaPersona, Peticion, Recuento, Semaforo } from './puente';

export const AZUL_PUENTE = '#12355b';

export function CabeceraPuente({
  titulo,
  subtitulo,
  onVolver,
  onActualizar,
  children,
}: {
  titulo: string;
  subtitulo?: string;
  onVolver: () => void;
  onActualizar?: () => void;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <View
      style={{
        backgroundColor: AZUL_PUENTE,
        color: '#ffffff',
        padding: `6px ${espacio.margen}px 12px`,
        gap: 8,
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: espacio.fila,
          minHeight: 56,
        }}
      >
        <BotonRedondo icono="cl" aria-label={t('Back')} onPress={onVolver} />
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text
            role="heading"
            aria-level={1}
            style={{
              ...texto.titulo,
              fontSize: 20,
              color: '#ffffff',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {titulo}
          </Text>
          {subtitulo && (
            <Text style={{ fontSize: 12.5, fontWeight: 700, color: '#c9d6e8' }}>
              {subtitulo}
            </Text>
          )}
        </View>
        {onActualizar && (
          <Button
            variant="bare"
            aria-label={t('Update now')}
            onPress={onActualizar}
            style={{
              minHeight: 40,
              padding: '0 12px',
              borderRadius: radio.boton,
              color: '#ffffff',
              fontWeight: 800,
              fontSize: 13,
              border: '1px solid rgba(255,255,255,0.35)',
            }}
          >
            <Trans>Update</Trans>
          </Button>
        )}
      </View>
      {children}
    </View>
  );
}

export function Seccion({
  titulo,
  derecha,
  children,
}: {
  titulo: string;
  derecha?: ReactNode;
  children: ReactNode;
}) {
  return (
    <View
      style={{
        backgroundColor: color.surface,
        borderRadius: 16,
        padding: '12px 14px',
        gap: 8,
        flexShrink: 0,
        boxShadow: '0 1px 2px rgba(20,33,61,0.08)',
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <Text style={{ ...texto.seccion, color: color.fg }}>{titulo}</Text>
        {derecha}
      </View>
      {children}
    </View>
  );
}

export function Dato({
  titulo,
  valor,
  tono,
}: {
  titulo: string;
  valor: ReactNode;
  tono?: 'ok' | 'warn' | 'bad';
}) {
  return (
    <View
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: 12,
        ...texto.cuerpo,
      }}
    >
      <Text style={{ color: color.fg2 }}>{titulo}</Text>
      <Text
        style={{
          fontWeight: 800,
          textAlign: 'right',
          color: tono ? color[tono] : color.fg,
        }}
      >
        {valor}
      </Text>
    </View>
  );
}

export function ListaRecuentos({
  filas,
  nombre,
  vacio,
  tono,
}: {
  filas: Recuento[];
  nombre: (clave: string) => string;
  vacio?: string;
  tono?: 'bad';
}) {
  const { t } = useTranslation();
  if (filas.length === 0) {
    return (
      <Text style={{ ...texto.cuerpo, color: color.fg2 }}>
        {vacio ?? t('No data yet.')}
      </Text>
    );
  }
  const maximo = Math.max(...filas.map(f => f.veces), 1);
  return (
    <>
      {filas.map(f => (
        <View key={f.clave} style={{ gap: 3 }}>
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              gap: 10,
              ...texto.secundario,
            }}
          >
            <Text
              style={{
                color: color.fg,
                minWidth: 0,
                overflowWrap: 'anywhere',
              }}
            >
              {nombre(f.clave)}
            </Text>
            <Text
              style={{
                fontWeight: 800,
                color: color.fg,
                whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {f.personas != null
                ? t('{{veces}} · {{count}} people', {
                    veces: f.veces,
                    count: f.personas,
                  })
                : f.veces}
            </Text>
          </View>
          <View
            style={{
              height: 5,
              borderRadius: 3,
              backgroundColor: color.surface2,
            }}
          >
            <View
              style={{
                width: `${Math.max(3, (f.veces / maximo) * 100)}%`,
                height: 5,
                borderRadius: 3,
                backgroundColor: tono === 'bad' ? color.bad : color.accent,
              }}
            />
          </View>
        </View>
      ))}
    </>
  );
}

const COLOR_SEMAFORO: Record<Semaforo, string> = {
  verde: color.ok,
  ambar: color.warn,
  rojo: color.bad,
};

export function Semaforito({ semaforo }: { semaforo: Semaforo }) {
  return (
    <View
      aria-hidden
      style={{
        width: 10,
        height: 10,
        borderRadius: 5,
        flexShrink: 0,
        backgroundColor: COLOR_SEMAFORO[semaforo],
      }}
    />
  );
}

export function Inicial({
  p,
  tamano: lado = 40,
}: {
  p: FilaPersona;
  tamano?: number;
}) {
  return (
    <View
      aria-hidden
      style={{
        width: lado,
        height: lado,
        borderRadius: lado / 2,
        flexShrink: 0,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: color.accentSoft,
        color: color.accent,
        fontWeight: 800,
        fontSize: lado * 0.42,
      }}
    >
      {inicialDe(p)}
    </View>
  );
}

export function FilaPeticion({
  p,
  onPersona,
  onCambio,
}: {
  p: Peticion;
  onPersona?: (id: string) => void;
  onCambio?: () => void;
}) {
  const { t } = useTranslation();
  const locale = useLanguage();
  const [estado, setEstado] = useState(p.estado);
  const [conFoto, setConFoto] = useState(Boolean(p.foto));
  const userId = p.userId;
  const marcar = async (nuevo: Peticion['estado']) => {
    try {
      await pedirPuente('/peticion/estado', {
        metodo: 'POST',
        cuerpo: { id: p.id, estado: nuevo },
      });
      setEstado(nuevo);
      // Hecha: el servidor borra la foto.
      if (nuevo === 'hecha') {
        setConFoto(false);
      }
      onCambio?.();
    } catch {
      // se queda como estaba
    }
  };
  return (
    <View
      style={{
        gap: 6,
        padding: '8px 0',
        borderTop: `1px solid ${color.line}`,
      }}
    >
      <Text style={{ ...texto.secundario, color: color.fg3 }}>
        {[
          p.tipo === 'fallo' ? t('Something is wrong') : t('Idea'),
          p.nombre,
          fechaCorta(p.creado, locale),
          p.pantalla ? nombrePantalla(t, p.pantalla) : null,
          p.version ? `v${p.version}` : null,
          p.aparato,
          estado === 'nueva'
            ? t('New')
            : estado === 'contestada'
              ? t('Answered')
              : t('Done'),
        ]
          .filter(Boolean)
          .join(' · ')}
      </Text>
      <View style={{ flexDirection: 'row', gap: 12, alignItems: 'flex-start' }}>
        {conFoto && <FotoPeticion id={p.id} />}
        <Text
          style={{
            ...texto.cuerpo,
            color: color.fg,
            whiteSpace: 'pre-wrap',
            flex: 1,
          }}
        >
          {p.texto}
        </Text>
      </View>
      <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
        {onPersona && userId && (
          <Boton variante="fantasma" onPress={() => onPersona(userId)}>
            <Trans>See person</Trans>
          </Boton>
        )}
        {estado !== 'contestada' && (
          <Boton variante="fantasma" onPress={() => void marcar('contestada')}>
            <Trans>Answered</Trans>
          </Boton>
        )}
        {estado !== 'hecha' && (
          <Boton variante="fantasma" onPress={() => void marcar('hecha')}>
            <Trans>Done</Trans>
          </Boton>
        )}
      </View>
    </View>
  );
}

/**
 * Miniatura de la foto de un aviso; al tocarla se ve en grande. La foto se
 * pide al servidor solo al pintar la fila y se suelta al quitarla.
 */
function FotoPeticion({ id }: { id: string }) {
  const { t } = useTranslation();
  const [url, setUrl] = useState<string | null>(null);
  const [fallo, setFallo] = useState(false);
  const [grande, setGrande] = useState(false);
  useEffect(() => {
    let vivo = true;
    let creada: string | null = null;
    pedirFotoPuente(id)
      .then(u => {
        creada = u;
        if (vivo) {
          setUrl(u);
        } else {
          URL.revokeObjectURL(u);
        }
      })
      .catch(() => {
        if (vivo) {
          setFallo(true);
        }
      });
    return () => {
      vivo = false;
      if (creada) {
        URL.revokeObjectURL(creada);
      }
    };
  }, [id]);
  if (fallo) {
    return null;
  }
  return (
    <>
      <Button
        variant="bare"
        aria-label={t('See the photo of the screen')}
        data-testid="peticion-foto"
        onPress={() => setGrande(true)}
        style={{
          width: 62,
          height: 134,
          borderRadius: 10,
          overflow: 'hidden',
          flexShrink: 0,
          padding: 0,
          backgroundColor: color.surface2,
          boxShadow: `0 0 0 1px ${color.line}`,
        }}
      >
        {url && (
          <img
            src={url}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'top',
            }}
          />
        )}
      </Button>
      {grande && url && (
        <Button
          variant="bare"
          aria-label={t('Close')}
          data-testid="peticion-foto-grande"
          onPress={() => setGrande(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 300,
            backgroundColor: 'rgba(14, 20, 36, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <img
            src={url}
            alt={t('Photo of the screen')}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              borderRadius: 16,
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
            }}
          />
        </Button>
      )}
    </>
  );
}
